import { useEffect, useState } from 'react';
import { Button, Grid, TextInput, Typography, CronInput, IconButton, Icon } from '@ts/storybook';
import { WrapperEntitySelector, Select } from '@components/Inputs';
import { GridBreak } from '@components/Utils';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { useAppDispatch } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { IForm } from '@ts/ui-models';

type TStatusIndicatorsDownstreamTransactionMode = 'distribute' | 'doNotDistribute';

interface IMobileAppSettings {
  schedule: string; // cron expression
  periods: number;
  interval: 'days' | 'weeks' | 'months';
  downstreamSnapshotCreationTimeout: number; // seconds
  statusIndicatorsDownstreamTransactionMode: {
    statusIndicator: string; // status indicator name
    mode: TStatusIndicatorsDownstreamTransactionMode;
  }[];
  items: string[]; // items custom ids
  itemTypes: {
    alwaysDownloadItemsOfItemTypesCustomIds: string[]; // item types custom ids
    neverDownloadItemsOfItemTypesCustomIds: string[]; // item types custom ids
  };
  eventTypes: string[]; // event types logical ids
}

interface IMobileAppSettingsFormProps extends IForm {
  values: {
    value: IMobileAppSettings;
  };
}

const listOptionsModes = [
  { value: 'distribute', label: 'Distribute' },
  { value: 'doNotDistribute', label: 'Do Not Distribute' },
];

const MobileAppSettingsForm = (props: IMobileAppSettingsFormProps) => {
  const { values, mode, onCallbackChange } = props;
  const defaultValues: IMobileAppSettings = {
    schedule: '0 22 * * *',
    periods: 8,
    interval: 'weeks',
    downstreamSnapshotCreationTimeout: 86400,
    statusIndicatorsDownstreamTransactionMode: [],
    items: [],
    itemTypes: {
      alwaysDownloadItemsOfItemTypesCustomIds: [],
      neverDownloadItemsOfItemTypesCustomIds: [],
    },
    eventTypes: [],
  };
  const mobileAppSettings: IMobileAppSettings = Object.assign({}, defaultValues, values.value ?? {});
  const intervals = [
    { label: 'Days', value: 'days' },
    { label: 'Weeks', value: 'weeks' },
    { label: 'Months', value: 'months' },
  ];

  useEffect(() => {
    onCallbackChange('value')(defaultValues);
  }, []);

  return (
    <Grid container spacing={2} px={4} pb={8}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" text="Snapshot creation schedule" />
        <CronInput value={mobileAppSettings.schedule} onChange={onCallbackChange('value.schedule')} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography variant="h6" text="Snapshot creation interval" />
        <TextInput
          //label="Snapshot creation interval"
          type="number"
          value={mobileAppSettings.downstreamSnapshotCreationTimeout}
          onChangeValue={onCallbackChange('value.downstreamSnapshotCreationTimeout')}
          isReadOnly={mode === 'view'}
        />
      </Grid>
      <GridBreak />
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" text="Time range to download" />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TextInput
          label="Periods"
          type="number"
          value={mobileAppSettings.periods}
          onChangeValue={onCallbackChange('value.periods')}
          min={1}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <Select
          label="Time interval"
          value={mobileAppSettings.interval}
          onChange={onCallbackChange('value.interval')}
          disabled={mode === 'view'}
          options={intervals}
        />
      </Grid>
      <GridBreak />
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" text="Items" />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('item')}
          //label="Items"
          value={mobileAppSettings.items}
          onChange={onCallbackChange('value.items')}
          multiple
          seeAllSelected
          fieldToReturn="customId"
        />
      </Grid>
      <GridBreak />
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" text="Always download item types" />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('itemType')}
          label="Item types"
          value={mobileAppSettings.itemTypes.alwaysDownloadItemsOfItemTypesCustomIds}
          onChange={onCallbackChange('value.itemTypes.alwaysDownloadItemsOfItemTypesCustomIds')}
          multiple
          seeAllSelected
          fieldToReturn="customId"
        />
      </Grid>
      <GridBreak />
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" text="Never download item types" />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('itemType')}
          label="Item types"
          value={mobileAppSettings.itemTypes.neverDownloadItemsOfItemTypesCustomIds}
          onChange={onCallbackChange('value.itemTypes.neverDownloadItemsOfItemTypesCustomIds')}
          multiple
          seeAllSelected
          fieldToReturn="customId"
        />
      </Grid>
      <GridBreak />
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" text="Event types" />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('eventType')}
          //label="Event types"
          value={mobileAppSettings.eventTypes}
          onChange={onCallbackChange('value.eventTypes')}
          multiple
          seeAllSelected
        />
      </Grid>
      <GridBreak />
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" text="Status indicators distribution mode" />
        <StatusTable
          statusData={
            Array.isArray(mobileAppSettings.statusIndicatorsDownstreamTransactionMode)
              ? mobileAppSettings.statusIndicatorsDownstreamTransactionMode
              : []
          }
          setStatusData={v => {
            onCallbackChange('value.statusIndicatorsDownstreamTransactionMode')(v);
          }}
        />
      </Grid>
    </Grid>
  );
};

interface IStatusTable {
  statusData: {
    statusIndicator: string;
    mode: TStatusIndicatorsDownstreamTransactionMode;
  }[];
  setStatusData: (statusData: { statusIndicator: string; mode: TStatusIndicatorsDownstreamTransactionMode }[]) => void;
}

const StatusTable = (props: IStatusTable) => {
  const dispatch = useAppDispatch();
  const { statusData, setStatusData } = props;

  const [newStatusIndicator, setNewStatusIndicator] = useState('');
  const [newMode, setNewMode] = useState<TStatusIndicatorsDownstreamTransactionMode>('doNotDistribute');

  //#region Handlers
  const handleAddNew = () => {
    if (!newStatusIndicator) return;

    setStatusData([...statusData, { statusIndicator: newStatusIndicator, mode: newMode }]);
    setNewStatusIndicator(''); // Reset input
    setNewMode('doNotDistribute'); // Reset select
  };

  const handleUpdate = (index: number, field: string, value: string) => {
    const updatedData = [...statusData];
    updatedData[index] = { ...updatedData[index], [field]: value };
    setStatusData(updatedData);
  };

  const handleDelete = (index: number) => {
    const updatedData = [...statusData];
    updatedData.splice(index, 1);

    setStatusData(updatedData);
  };
  //#endregion

  return (
    <Table>
      <TableBody>
        {statusData.map((status, index) => (
          <StatusRow
            key={status.statusIndicator}
            statusIndicator={status.statusIndicator}
            mode={status.mode as TStatusIndicatorsDownstreamTransactionMode}
            onDelete={() => handleDelete(index)}
            onUpdate={(field, value) => handleUpdate(index, field, value)}
          />
        ))}
        <TableRow>
          <TableCell
            sx={{
              width: {
                xs: '33.33%',
                md: '50%',
              },
            }}
          >
            <WrapperEntitySelector
              searchComponent={generateSearchEntitySelector('indicatorType')}
              label="Status indicator"
              value={newStatusIndicator}
              onChange={value => {
                if (statusData.some(s => s.statusIndicator === value)) {
                  dispatch(globalActions.notification('Status indicator already exists'));
                  setNewStatusIndicator('');
                } else {
                  setNewStatusIndicator(value as string);
                }
                //onUpdate('statusIndicator', value as string);
              }}
              fieldToReturn="name"
              withoutId
            />
          </TableCell>
          <TableCell
            sx={{
              width: {
                xs: '33.33%',
                md: '50%',
              },
            }}
          >
            <Select
              label="Mode"
              value={newMode}
              onChange={value => {
                setNewMode(value as TStatusIndicatorsDownstreamTransactionMode);
                //onUpdate('mode', value);
              }}
              options={listOptionsModes}
            />
          </TableCell>
          <TableCell>
            <Button
              startIcon={<Icon icon="add" />}
              onClick={handleAddNew}
              disabled={!newStatusIndicator || statusData.some(s => s.statusIndicator === newStatusIndicator)}
              label="Add"
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

interface IStatusRowProps {
  statusIndicator: string;
  mode: TStatusIndicatorsDownstreamTransactionMode;
  onDelete: () => void;
  onUpdate: (field: string, value: string) => void;
}

const StatusRow = ({ statusIndicator, mode, onDelete, onUpdate }: IStatusRowProps) => {
  const [localStatusIndicator, setLocalStatusIndicator] = useState(statusIndicator);
  const [localMode, setLocalMode] = useState(mode);

  return (
    <TableRow>
      <TableCell
        sx={{
          width: {
            xs: '33.33%',
            md: '50%',
          },
        }}
      >
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('indicatorType')}
          label="Status indicator"
          value={localStatusIndicator}
          onChange={value => {
            setLocalStatusIndicator(value as string);
            onUpdate('statusIndicator', value as string);
          }}
          fieldToReturn="name"
          withoutId
        />
      </TableCell>
      <TableCell
        sx={{
          width: {
            xs: '33.33%',
            md: '50%',
          },
        }}
      >
        <Select
          value={localMode}
          label="Mode"
          onChange={value => {
            setLocalMode(value as TStatusIndicatorsDownstreamTransactionMode);
            onUpdate('mode', value as string);
          }}
          options={listOptionsModes}
        />
      </TableCell>
      <TableCell>
        <IconButton icon="delete" onClick={onDelete} />
      </TableCell>
    </TableRow>
  );
};

export default MobileAppSettingsForm;
